/* Isolated from video-media: the original section remains the comparison baseline. */
(() => {
  if (customElements.get('optimized-column-video')) return;
  class OptimizedColumnVideo extends HTMLElement {
    connectedCallback() {
      // Wait until parser-created children exist. Cloned cards retain the inert template.
      this.setupFrame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.visible = false;
        this.setAttribute('aria-busy', this.hasAttribute('loaded') ? 'false' : 'true');
        this.observer = new IntersectionObserver(entries => {
          for (const entry of entries) {
            this.visible = entry.isIntersecting;
            if (this.visible) {
              this.abortRetryAvailable = true;
              this.activate();
            }
            else this.video?.pause();
          }
        });
        this.observer.observe(this);
        this.visibilityHandler = () => {
          if (document.hidden) this.video?.pause();
          else if (this.visible) this.activate();
        };
        document.addEventListener('visibilitychange', this.visibilityHandler);
      });
    }

    activate() {
      if (document.hidden) return;
      if (!this.video) {
        const template = this.querySelector('template');
        const source = template?.content.querySelector('video');
        if (!source) return;
        const video = source.cloneNode(true);
        this.video = video;
        if (this.hasAttribute('autoplay')) {
          video.muted = true;
          video.defaultMuted = true;
        }
        video.playsInline = true;
        video.autoplay = false;
        video.preload = 'auto';
        // Keep the template in place, so carousel clones never depend on source timing.
        video.addEventListener('loadeddata', () => {
          if ((!video.requestVideoFrameCallback || !this.hasAttribute('autoplay')) && video.readyState >= 2) {
            requestAnimationFrame(() => {
              if (this.isConnected && this.video === video) this.revealFrame();
            });
          }
        });
        video.addEventListener('canplay', () => {
          if (this.visible && this.hasAttribute('autoplay')) this.startPlayback();
        });
        video.addEventListener('error', () => {
          this.removeAttribute('loaded');
          this.setAttribute('aria-busy', 'false');
          this.setAttribute('media-error', String(video.error?.code || 'unknown'));
          console.error('Optimized multicolumn video failed', video.currentSrc, video.error);
        });
        this.appendChild(video);
        // Exactly once, after insertion and playback properties have been set.
        video.load();
      }
      if (this.hasAttribute('autoplay')) this.startPlayback();
    }

    startPlayback() {
      const video = this.video;
      if (!video || !this.visible || document.hidden || this.playPending || !video.paused) return;
      this.playPending = true;
      let interrupted = false;
      video.play().then(() => {
        if (!this.isConnected || !this.visible || document.hidden) {
          video.pause();
          return;
        }
        this.removeAttribute('suspended');
        if (video.requestVideoFrameCallback && this.frameCallback == null) {
          this.frameCallback = video.requestVideoFrameCallback(() => {
            this.frameCallback = null;
            if (this.isConnected && this.video === video) this.revealFrame();
          });
        }
      }).catch(error => {
        // A visibility-triggered pause can interrupt a pending play. Re-entry retries.
        interrupted = error.name === 'AbortError';
        if (!interrupted) {
          this.setAttribute('suspended', '');
          this.setAttribute('aria-busy', 'false');
          console.warn('Optimized multicolumn playback rejected', video.currentSrc, error);
        }
      }).finally(() => {
        this.playPending = false;
        if (interrupted && this.abortRetryAvailable && this.visible && !document.hidden) {
          this.abortRetryAvailable = false;
          // Only retry a visibility race, never an error or an autoplay rejection.
          if (this.isConnected && this.video === video) this.startPlayback();
        }
      });
    }

    revealFrame() {
      this.setAttribute('loaded', '');
      this.setAttribute('aria-busy', 'false');
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.setupFrame);
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      if (this.frameCallback != null) this.video?.cancelVideoFrameCallback(this.frameCallback);
      this.frameCallback = null;
      this.video?.pause();
    }
  }
  customElements.define('optimized-column-video', OptimizedColumnVideo);
})();

/* One owner per section for layout, loop navigation, and the community dialog. */
(() => {
  if (customElements.get('optimized-multicolumn')) return;

  // Normalize the three historical RTL scrollLeft models without changing page direction.
  let rtlModel;
  function getRtlModel() {
    if (rtlModel) return rtlModel;
    const probe = document.createElement('div');
    probe.dir = 'rtl';
    probe.style.cssText = 'position:absolute;inset-inline-start:-9999px;width:4px;height:1px;overflow:scroll;visibility:hidden';
    const child = document.createElement('div');
    child.style.width = '8px';
    probe.append(child);
    document.body.append(probe);
    if (probe.scrollLeft > 0) rtlModel = 'default';
    else {
      probe.scrollLeft = 1;
      rtlModel = probe.scrollLeft === 0 ? 'negative' : 'reverse';
    }
    probe.remove();
    return rtlModel;
  }

  class OptimizedMulticolumn extends HTMLElement {
    connectedCallback() {
      // Also works when the Theme Editor inserts parsed markup or reconnects a section.
      this.setupFrame = requestAnimationFrame(() => this.setup());
    }

    setup() {
      if (!this.isConnected || this.controller) return;
      this.viewport = this.querySelector('[data-om-viewport]');
      this.track = this.querySelector('[data-om-track]');
      if (!this.viewport || !this.track) return;
      this.originals = Array.from(this.track.children).filter(card => !card.hasAttribute('data-om-clone'));
      this.controls = this.querySelector('[data-om-controls]');
      this.buttons = Array.from(this.querySelectorAll('[data-om-direction]'));
      this.dataset.cardCount = String(this.originals.length);
      this.dataset.mobileColumns = getComputedStyle(this).getPropertyValue('--om-columns-mobile').trim() || '1';
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.desktop = matchMedia('(min-width: 1024px)');
      this.mobileNavigation = matchMedia('(max-width: 767px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.addEventListener('click', event => this.onClick(event), { signal });
      this.addEventListener('keydown', event => this.onKeydown(event), { signal });
      this.addEventListener('focusin', event => this.onFocus(event), { signal });
      this.addEventListener('shopify:block:select', event => this.selectBlock(event), { signal });
      this.viewport.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      this.viewport.addEventListener('scrollend', () => this.settle(), { signal });
      // Interrupt arrow animation on direct manipulation, preserving native touch scrolling.
      this.viewport.addEventListener('pointerdown', () => { this.dragging = true; this.stopAnimation(); }, { passive: true, signal });
      document.addEventListener('pointerup', () => { this.dragging = false; this.scheduleSettle(); }, { passive: true, signal });
      document.addEventListener('pointercancel', () => { this.dragging = false; this.scheduleSettle(); }, { passive: true, signal });
      this.viewport.addEventListener('wheel', () => this.stopAnimation(), { passive: true, signal });
      this.desktop.addEventListener('change', () => this.scheduleLayout(), { signal });
      this.reducedMotion.addEventListener('change', () => { this.stopAnimation(); this.settle(); }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
      this.resizeObserver.observe(this.viewport);
      this.section = this.closest('.shopify-section') || this;
      this.section.addEventListener('shopify:section:unload', () => this.teardown(), { signal });
      this.layout();
    }

    get position() {
      const raw = this.viewport.scrollLeft;
      if (!this.rtl) return raw;
      const model = getRtlModel();
      if (model === 'negative') return -raw;
      if (model === 'default') return this.maxPosition - raw;
      return raw;
    }

    get maxPosition() { return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth); }

    setPosition(value) {
      const model = this.rtl ? getRtlModel() : null;
      this.viewport.scrollLeft = model === 'negative' ? -value : model === 'default' ? this.maxPosition - value : value;
    }

    scheduleLayout() {
      if (this.layoutFrame || !this.controller) return;
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = null;
        // Height-only observations and duplicate resize notifications should not rebuild players.
        const signature = this.layoutSignature();
        if (signature !== this.signature) this.layout();
        else this.positionNavigation();
      });
    }

    layoutSignature() {
      const styles = getComputedStyle(this.viewport);
      return [this.viewport.clientWidth, this.desktop.matches, styles.direction,
        styles.getPropertyValue('--om-visible-columns'), styles.getPropertyValue('--om-active-gap')].join('|');
    }

    layout() {
      if (!this.controller) return;
      const oldStep = this.step || 1;
      const index = this.resumeIndex ?? Math.round((this.position - (this.base || 0)) / oldStep);
      this.resumeIndex = null;
      this.stopAnimation();
      clearTimeout(this.settleTimer);
      const previousSnap = this.viewport.style.scrollSnapType;
      this.viewport.style.scrollSnapType = 'none';
      this.removeClones();
      this.loop = false;
      this.base = 0;
      this.rtl = getComputedStyle(this.viewport).direction === 'rtl';
      this.carousel = this.desktop.matches ? this.dataset.desktopCarousel === 'true' : this.dataset.mobileCarousel === 'true' || this.dataset.mode === 'grid';
      this.toggleAttribute('data-carousel-active', this.carousel);
      this.viewport.setAttribute('aria-roledescription', this.carousel ? 'carousel' : 'grid');
      this.viewport.removeAttribute('tabindex');
      if (!this.originals.length) { this.controls.hidden = true; this.viewport.style.scrollSnapType = previousSnap; return; }
      const first = this.originals[0].getBoundingClientRect();
      const second = this.originals[1]?.getBoundingClientRect();
      this.step = second ? Math.abs(second.left - first.left) : first.width;
      // Four-column desktop carousels remain interactive even when exactly four cards fit.
      const fourColumnLoop = this.desktop.matches && this.carousel && this.originals.length >= 4
        && Number(getComputedStyle(this.viewport).getPropertyValue('--om-visible-columns')) === 4;
      this.overflow = this.carousel && (this.maxPosition > 1 || fourColumnLoop) && this.step > 1;
      if (this.overflow && (this.dataset.loop === 'true' || fourColumnLoop) && this.originals.length > 1) {
        this.period = this.step * this.originals.length;
        // Only enough cards to cover one viewport plus a navigation step on either edge.
        // Never duplicate more than one sequence per side, including an exactly fitting row.
        const count = Math.min(this.originals.length, Math.ceil(this.viewport.clientWidth / this.step) + 1);
        const head = document.createDocumentFragment();
        const tail = document.createDocumentFragment();
        for (let i = this.originals.length - count; i < this.originals.length; i++) head.append(this.cloneCard(this.originals[i]));
        for (let i = 0; i < count; i++) tail.append(this.cloneCard(this.originals[i]));
        this.track.prepend(head);
        this.track.append(tail);
        this.base = count * this.step;
        this.loop = true;
      }
      const canonicalIndex = ((index % this.originals.length) + this.originals.length) % this.originals.length;
      this.jump(this.overflow ? (this.loop ? this.base + canonicalIndex * this.step : Math.min(Math.max(0, index * this.step), this.maxPosition)) : 0);
      if (this.overflow) this.viewport.tabIndex = 0;
      this.controls.hidden = !this.overflow || this.dataset.mode === 'grid';
      this.viewport.style.scrollSnapType = previousSnap;
      this.updateButtons();
      this.positionNavigation();
      this.signature = this.layoutSignature();
    }

    positionNavigation() {
      // Reuse the same controls over images on desktop/mobile; preserve the tablet header.
      const mobile = this.mobileNavigation.matches;
      const overlay = mobile || this.desktop.matches;
      const host = this.querySelector(overlay ? '.om-gallery-container' : '.om-heading-container');
      if (!host) return;
      if (this.controls.parentElement !== host) host.append(this.controls);
      if (overlay && !this.controls.hidden) {
        const media = this.originals[0]?.querySelector('.om-card__media, .community-photo-card') || this.viewport;
        const image = media.getBoundingClientRect();
        const gallery = host.getBoundingClientRect();
        this.controls.style.setProperty('--om-navigation-image-center', `${image.top - gallery.top + image.height / 2}px`);
      }
      else this.controls.style.removeProperty('--om-navigation-image-center');
    }

    cloneCard(card) {
      const clone = card.cloneNode(true);
      clone.setAttribute('data-om-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id], [data-shopify-editor-block]').forEach(node => {
        node.removeAttribute('id');
        node.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(node => node.tabIndex = -1);
      clone.querySelectorAll('optimized-column-video').forEach(media => {
        ['loaded', 'playing', 'suspended', 'media-error'].forEach(attr => media.removeAttribute(attr));
        // Keep inert templates, but discard hydrated players and their transient state.
        media.querySelectorAll('video, iframe').forEach(node => node.remove());
      });
      return clone;
    }

    removeClones() { this.track.querySelectorAll('[data-om-clone]').forEach(card => card.remove()); }

    jump(value) {
      // Disable snapping during an equivalent-position correction, then restore it synchronously.
      const snap = this.viewport.style.scrollSnapType;
      this.viewport.style.scrollSnapType = 'none';
      this.setPosition(value);
      void this.viewport.offsetWidth;
      this.viewport.style.scrollSnapType = snap;
    }

    wrap() {
      if (!this.loop) return;
      const relative = this.position - this.base;
      let wrapped = ((relative % this.period) + this.period) % this.period;
      // Fractional card widths may round a snap position just below the period boundary.
      if (wrapped < 1 || this.period - wrapped < 1) wrapped = 0;
      const target = this.base + wrapped;
      if (Math.abs(target - this.position) > 0.5) this.jump(target);
    }

    navigate(direction) {
      if (!this.overflow) return;
      this.stopAnimation();
      this.wrap();
      const from = this.position;
      const stepIndex = Math.round((from - this.base) / this.step) + direction;
      let target = this.base + stepIndex * this.step;
      if (!this.loop) target = Math.min(this.maxPosition, Math.max(0, target));
      if (this.reducedMotion.matches || Math.abs(target - from) < 1) {
        this.jump(target);
        this.settle();
        return;
      }
      this.setAttribute('data-animating', '');
      const start = performance.now();
      const tick = now => {
        if (!this.controller) return;
        const progress = Math.min(1, (now - start) / 280);
        this.setPosition(from + (target - from) * (1 - Math.pow(1 - progress, 3)));
        if (progress < 1) this.animationFrame = requestAnimationFrame(tick);
        else { this.animationFrame = null; this.removeAttribute('data-animating'); this.settle(); }
      };
      this.animationFrame = requestAnimationFrame(tick);
    }

    stopAnimation() {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.removeAttribute('data-animating');
    }

    updateButtons() {
      for (const button of this.buttons) {
        const previous = button.dataset.omDirection === '-1';
        button.disabled = !this.overflow || (!this.loop && (previous ? this.position <= 1 : this.position >= this.maxPosition - 1));
      }
    }

    scheduleSettle() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), 140);
    }

    onScroll() { this.updateButtons(); this.scheduleSettle(); }

    settle() {
      if (!this.controller || this.animationFrame || this.dragging) return;
      clearTimeout(this.settleTimer);
      this.wrap();
      this.updateButtons();
    }

    selectBlock(event) {
      const card = this.originals.find(item => item === event.target || item.contains(event.target));
      if (!card || !this.carousel) return;
      this.stopAnimation();
      this.jump(this.base + this.originals.indexOf(card) * this.step);
      this.updateButtons();
    }

    onFocus(event) {
      const card = event.target.closest('[data-om-card]');
      if (!card || !this.carousel || !this.track.contains(card)) return;
      // Keyboard navigation always brings the real editor/content block into view.
      const original = this.originals.find(item => item.dataset.blockId === card.dataset.blockId);
      if (original && card !== original) {
        const targets = Array.from(card.querySelectorAll('a,button,[tabindex]'));
        const equivalent = original.querySelectorAll('a,button,[tabindex]')[targets.indexOf(event.target)];
        equivalent?.focus({ preventScroll: true });
        return;
      }
      const rect = card.getBoundingClientRect();
      const viewport = this.viewport.getBoundingClientRect();
      if (rect.left < viewport.left - 1 || rect.right > viewport.right + 1) this.selectBlock({ target: card });
    }

    onClick(event) {
      const button = event.target.closest('[data-om-direction]');
      if (button && this.contains(button)) {
        event.preventDefault();
        this.navigate(Number(button.dataset.omDirection));
        return;
      }
      if (event.target.closest('[data-community-photo-close]')) { this.closeModal(); return; }
      const trigger = event.target.closest('[data-community-photo-trigger]');
      if (trigger && this.contains(trigger)) {
        event.preventDefault();
        this.openModal(trigger);
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.closeModal();
      if (event.key === 'Tab' && this.modal && !this.modal.hidden && !window.theme?.a11y) {
        const targets = Array.from(this.modal.querySelectorAll('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])'));
        const first = targets[0];
        const last = targets[targets.length - 1];
        if (event.shiftKey && event.target === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && event.target === last) { event.preventDefault(); first?.focus(); }
      }
      if (event.target !== this.viewport || !this.overflow) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigate((event.key === 'ArrowRight' ? 1 : -1) * (this.rtl ? -1 : 1));
      }
    }

    openModal(trigger) {
      const modal = this.querySelector('[data-community-photo-modal]');
      const template = this.originals.flatMap(card => Array.from(card.querySelectorAll('template[id]')))
        .find(item => item.id === trigger.dataset.templateId);
      if (!modal || !template) return;
      OptimizedMulticolumn.activeModal?.closeModal();
      this.closeModal();
      OptimizedMulticolumn.activeModal = this;
      this.modal = modal;
      this.activeTrigger = trigger;
      this.modalContent = modal.querySelector('[data-community-photo-modal-content]');
      this.modalContent.replaceChildren(template.content.cloneNode(true));
      modal.hidden = false;
      this.ownedBodyLock = !document.body.classList.contains('has-modal-open');
      document.body.classList.add('has-modal-open');
      const dialog = modal.querySelector('[role="dialog"]');
      const close = dialog.querySelector('[data-community-photo-close]');
      if (window.theme?.a11y) window.theme.a11y.trapFocus(dialog, close || dialog);
      else (close || dialog).focus();
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true;
      this.modalContent.replaceChildren();
      if (this.ownedBodyLock) document.body.classList.remove('has-modal-open');
      if (window.theme?.a11y) window.theme.a11y.removeTrapFocus(this.activeTrigger?.isConnected ? this.activeTrigger : null);
      else if (this.activeTrigger?.isConnected) this.activeTrigger.focus({ preventScroll: true });
      this.activeTrigger = null;
      if (OptimizedMulticolumn.activeModal === this) OptimizedMulticolumn.activeModal = null;
    }

    teardown() {
      if (this.controller && this.overflow) this.resumeIndex = Math.round((this.position - this.base) / this.step);
      cancelAnimationFrame(this.setupFrame);
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = null;
      clearTimeout(this.settleTimer);
      this.stopAnimation();
      this.closeModal();
      this.resizeObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
      if (this.track) this.removeClones();
      this.removeAttribute('data-carousel-active');
      if (this.controls) this.controls.hidden = true;
    }

    disconnectedCallback() { this.teardown(); }
  }
  customElements.define('optimized-multicolumn', OptimizedMulticolumn);
})();
